import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import pieces from '../data/pieces';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Collection() {
  const navigate = useNavigate();
  const [gridRef, gridVis] = useReveal(0.05);

  // Produit sélectionné — overlay plein écran, zéro changement de page
  const [expanding, setExpanding] = useState(null);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [detailPhase, setDetailPhase] = useState(0);

  const handlePieceClick = (piece, e) => {
    const img = e.currentTarget.querySelector('img');
    if (!img) return;
    const rect = img.getBoundingClientRect();
    setExpanding({ piece, rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height } });

    setTimeout(() => {
      setSelectedPiece(piece);
      setDetailPhase(1);
      setExpanding(null);
      window.history.pushState({}, '', `/collection/${piece.id}`);
    }, 1200);
  };

  const handleCloseDetail = () => {
    setDetailPhase(0);
    setTimeout(() => {
      setSelectedPiece(null);
      window.history.pushState({}, '', '/collection');
    }, 400);
  };

  const [navKey, setNavKey] = useState(0); // force re-render des animations
  const [navDir, setNavDir] = useState('next'); // direction pour l'animation

  const handleNavPiece = (newPiece, dir) => {
    setNavDir(dir);
    setNavKey(k => k + 1);
    setSelectedPiece(newPiece);
    window.history.replaceState({}, '', `/collection/${newPiece.id}`);
  };

  useEffect(() => {
    const onPopState = () => {
      if (selectedPiece) {
        setDetailPhase(0);
        setTimeout(() => setSelectedPiece(null), 400);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [selectedPiece]);

  // Scroll-driven parallax sur la section intro
  const heroRef = useRef(null);
  const [sp, setSp] = useState(0);

  const handleScroll = useCallback(() => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const total = heroRef.current.offsetHeight - window.innerHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / total));
    setSp(progress);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Phases scroll :
  // 0.00 - 0.20 : Titre "Rêverie" visible seul
  // 0.15 - 0.30 : Titre fade out
  // 0.25 - 0.55 : Texte complet (2 paragraphes + tags) ensemble
  // 0.50 - 0.65 : Texte fade out
  // 0.60 - 1.00 : Aperçu images (grille dans le sticky)

  const titleOp = sp < 0.15 ? 1 : Math.max(0, 1 - (sp - 0.15) / 0.12);
  const titleY = sp * -60;

  const textOp = (() => {
    if (sp < 0.25) return 0;
    if (sp < 0.35) return (sp - 0.25) / 0.10;
    if (sp < 0.50) return 1;
    if (sp < 0.60) return 1 - (sp - 0.50) / 0.10;
    return 0;
  })();
  const textY = (1 - textOp) * 20;

  const imgOp = sp < 0.58 ? 0 : Math.min(1, (sp - 0.58) / 0.15);
  const imgScale = 0.85 + imgOp * 0.15;

  return (
    <div>
      {/* ── EXPAND ANIMATION — image qui grandit ── */}
      {expanding && (
        <div className="fixed inset-0 z-[200] pointer-events-none">
          <div style={{
            position: 'absolute',
            top: expanding.rect.top,
            left: expanding.rect.left,
            width: expanding.rect.width,
            height: expanding.rect.height,
            backgroundImage: `url(${expanding.piece.img})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: 'expandToFull 1.1s cubic-bezier(0.22, 1, 0.36, 1) forwards, dimImage 0.8s ease forwards 0.3s',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)',
            opacity: 0,
            animation: 'fadeIn 0.5s ease forwards 0.4s',
          }} />
          <style>{`
            @keyframes expandToFull { to { top:0; left:0; width:100vw; height:100vh; } }
            @keyframes dimImage { to { filter: brightness(0.4) contrast(1.1) saturate(0.85); } }
            @keyframes fadeIn { to { opacity:1; } }
          `}</style>
        </div>
      )}

      {/* ── PRODUCT DETAIL OVERLAY — rendu inline, pas de changement de page ── */}
      {selectedPiece && (() => {
        const p = selectedPiece;
        const prevPiece = pieces.find(x => x.id === p.id - 1);
        const nextPiece = pieces.find(x => x.id === p.id + 1);
        const show = detailPhase >= 1;
        // Direction du slide : next = texte vient de droite, prev = texte vient de gauche
        const slideFrom = navDir === 'next' ? 'translate-x-8' : '-translate-x-8';
        const slideFromY = navDir === 'next' ? 'translate-y-[-45%]' : 'translate-y-[-55%]';
        return (
          <div key={navKey} className={`fixed inset-0 z-[150] bg-black transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}>
            {/* Image avec crossfade */}
            <img key={`img-${navKey}`} src={p.img} alt={p.name}
              className="absolute inset-0 w-full h-full object-cover animate-imgReveal"
              style={{ filter: 'brightness(0.4) contrast(1.1) saturate(0.85)' }} />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.5)' }} />

            {/* Retour */}
            <button onClick={handleCloseDetail}
              className="absolute top-24 left-8 md:left-12 flex items-center gap-3 text-white/50 hover:text-white transition-all z-10 group duration-500">
              <span className="w-8 h-px bg-current transition-all duration-300 group-hover:w-14" />
              <span className="text-[11px] tracking-[0.2em] uppercase">Retour</span>
            </button>

            {/* Nom — slide depuis le côté */}
            <div key={`name-${navKey}`} className={`absolute top-1/4 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-x-0' : `opacity-0 ${slideFrom}`}`}
              style={{ transitionDelay: '0.05s' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-white/30" />
                <p className="text-[10px] tracking-[0.5em] uppercase text-white/40">{p.type}</p>
              </div>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light leading-none tracking-wide">
                {p.name}
              </h1>
            </div>

            {/* Histoire — léger décalage */}
            <div key={`story-${navKey}`} className={`absolute top-1/2 left-8 md:left-12 lg:left-16 z-10 max-w-md transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-[-50%]' : `opacity-0 ${slideFromY}`}`}
              style={{ marginTop: '60px', transitionDelay: '0.12s' }}>
              <p className="text-white/50 text-[15px] leading-[1.9] font-light">{p.story}</p>
            </div>

            {/* Détails — slide up */}
            <div key={`details-${navKey}`} className={`absolute bottom-28 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
              style={{ transitionDelay: '0.18s' }}>
              <div className="flex gap-10">
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Pierre</p>
                  <p className="font-serif text-lg text-white/80">{p.stone}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Origine</p>
                  <p className="font-serif text-lg text-white/80">{p.origin}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Statut</p>
                  <p className="font-serif text-lg text-white/80 capitalize">{p.status}</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div key={`cta-${navKey}`} className={`absolute bottom-10 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[600ms] ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
              style={{ transitionDelay: '0.22s' }}>
              <Link to={`/contact?piece=${encodeURIComponent(p.name)}`}
                className="inline-block px-8 py-3 border border-white/20 text-white/70 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500">
                Me renseigner
              </Link>
            </div>

            {/* Nav prev/next — toujours visible */}
            <div className="absolute bottom-10 right-8 md:right-12 lg:right-16 z-10 flex items-center gap-8">
              {prevPiece && (
                <button onClick={() => handleNavPiece(prevPiece, 'prev')}
                  className="text-[11px] tracking-[0.2em] uppercase text-white/30 hover:text-white transition-colors flex items-center gap-3 group">
                  <span className="w-6 h-px bg-current transition-all duration-300 group-hover:w-12" />
                  Préc.
                </button>
              )}
              {nextPiece && (
                <button onClick={() => handleNavPiece(nextPiece, 'next')}
                  className="text-[11px] tracking-[0.2em] uppercase text-white/30 hover:text-white transition-colors flex items-center gap-3 group">
                  Suiv.
                  <span className="w-6 h-px bg-current transition-all duration-300 group-hover:w-12" />
                </button>
              )}
            </div>

            {/* Compteur */}
            <div key={`count-${navKey}`} className={`absolute bottom-10 left-1/2 -translate-x-1/2 z-10 transition-all duration-[600ms] ${show ? 'opacity-100' : 'opacity-0'}`}
              style={{ transitionDelay: '0.15s' }}>
              <p className="text-[10px] tracking-[0.4em] text-white/15 uppercase">
                {String(p.id).padStart(2, '0')} / {String(pieces.length).padStart(2, '0')}
              </p>
            </div>

            <style>{`
              @keyframes imgReveal {
                from { opacity: 0; transform: scale(1.05); }
                to { opacity: 1; transform: scale(1); }
              }
              .animate-imgReveal {
                animation: imgReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
              }
            `}</style>
          </div>
        );
      })()}

      {/* ── SCROLL-DRIVEN INTRO ── */}
      <section ref={heroRef} className="relative bg-white" style={{ height: '350vh' }}>
        <div className="sticky top-0 h-screen flex items-center justify-center px-6">

          {/* Fond : ligne décorative qui bouge */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="w-px bg-gray-100 transition-all duration-300"
              style={{ height: `${100 + sp * 200}px`, opacity: 0.5 + sp * 0.5 }} />
          </div>

          {/* Phase 1 : Titre "Rêverie" */}
          <div className="absolute inset-0 flex items-center justify-center"
            style={{ opacity: titleOp, transform: `translateY(${titleY}px)` }}>
            <div className="text-center">
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-10 h-px bg-gray-200" />
                <div className="w-1.5 h-1.5 rotate-45 bg-gray-900" />
                <div className="w-10 h-px bg-gray-200" />
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-gray-900 italic font-light">
                Rêverie
              </h1>
              <div className="w-12 h-px bg-gray-200 mx-auto mt-8" />
            </div>
          </div>

          {/* Phase 2 : Texte complet (2 paragraphes + tags ensemble) */}
          <div className="absolute inset-0 flex items-center justify-center px-6"
            style={{ opacity: textOp, transform: `translateY(${textY}px)` }}>
            <div className="text-center max-w-xl">
              <p className="text-gray-500 text-lg md:text-xl leading-[2] font-light mb-6">
                Rêverie est une collection d'instants suspendus.
                Elle évoque les moments où la pensée vagabonde...
              </p>
              <p className="text-gray-500 text-lg md:text-xl leading-[2] font-light mb-10">
                C'est à Bangkok, là où tout a commencé pour Pernety,
                que nous avons d'abord rêvé.
              </p>
              <div className="flex items-center justify-center gap-4 md:gap-6 text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.3em] text-gray-300 font-light whitespace-nowrap">
                <span>Pièces uniques</span>
                <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                <span>Sur commande</span>
                <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                <span>Recherche de pierres sur demande</span>
              </div>
            </div>
          </div>

          {/* Phase 3 : Le vrai bento grid qui apparait */}
          <div className="absolute inset-0 flex items-center justify-center px-2 md:px-4"
            style={{ opacity: imgOp, transform: `scale(${imgScale})`, pointerEvents: imgOp >= 0.95 ? 'auto' : 'none' }}>
            <div ref={gridRef} className="w-full max-w-7xl mx-auto">
              <div className="grid grid-cols-5 md:grid-cols-7 gap-1.5 md:gap-2"
                style={{ gridAutoRows: 'calc((100vh - 120px) / 4)' }}>

                {[
                  { idx: 0, cls: 'col-span-2 row-span-2' },
                  { idx: 1, cls: 'col-span-2 row-span-1' },
                  { idx: 2, cls: 'col-span-1 row-span-1' },
                  { idx: 3, cls: 'col-span-2 row-span-2' },
                  { idx: 4, cls: 'col-span-1 row-span-1' },
                  { idx: 5, cls: 'col-span-1 row-span-1' },
                  { idx: 6, cls: 'col-span-1 row-span-1' },
                  { idx: 7, cls: 'col-span-2 row-span-1' },
                  { idx: 8, cls: 'col-span-1 row-span-1' },
                  { idx: 9, cls: 'col-span-2 row-span-1' },
                  { idx: 10, cls: 'col-span-1 row-span-1' },
                  { idx: 11, cls: 'col-span-2 row-span-1' },
                  { idx: 12, cls: 'col-span-2 row-span-1' },
                ].map(({ idx, cls }) => (
                  <BentoItem key={idx} piece={pieces[idx]} className={cls} vis={imgOp > 0.3} delay={idx * 60} onClick={handlePieceClick} />
                ))}

                {/* Signature collection — bas droite */}
                <div className={`col-span-3 row-span-1 flex flex-col items-center justify-center transition-all duration-1000 ${imgOp > 0.5 ? 'opacity-100' : 'opacity-0'}`}
                  style={{ transitionDelay: '800ms' }}>
                  <img src="/photos/Logo_fd_transparent.png" alt="Pernety" className="h-10 w-auto mb-3 opacity-15" />
                  <p className="font-serif text-2xl md:text-3xl text-gray-200 italic font-light tracking-wide">Rêverie</p>
                  <div className="w-8 h-px bg-gray-200 mt-2" />
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SUR COMMANDE ── */}
      <section className="py-24 md:py-32 px-6 bg-stone-50">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="w-16 h-px bg-gray-200" />
            <div className="w-3 h-3 rotate-45 border border-gray-300" />
            <div className="w-16 h-px bg-gray-200" />
          </div>

          <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6 leading-tight">
            Pièces réalisées sur commande
          </h2>
          <p className="text-gray-400 text-base leading-relaxed mb-4">
            À partir de pierres sélectionnées avec soin.
          </p>
          <p className="text-gray-400 text-base leading-relaxed mb-10">
            Recherche de pierres sur demande.
          </p>

          <Link to="/contact"
            className="inline-block px-12 py-5 bg-black text-white text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-gray-800">
            Demander une création
          </Link>
        </div>
      </section>
    </div>
  );
}

function BentoItem({ piece, className, vis, delay, onClick }) {
  return (
    <div className={`group relative overflow-hidden cursor-pointer transition-all duration-1000 ${className} ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
      onClick={(e) => onClick(piece, e)}>

      <img src={piece.img} alt={piece.name}
        className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110"
        loading="lazy" />

      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-500 flex items-center justify-center">
        <p className="text-white font-serif text-base md:text-lg tracking-wider opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
          {piece.name}
        </p>
      </div>
    </div>
  );
}
